import { CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';

/**
 * The Search module which handles listing filters and search functionality.
 * @class Search
 */
export default class Search {
  /**
   * Search constructor which assigns the element passed into the constructor
   * to the `this.element` property for later reference
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    /**
     * Reference to the DOM element that is the root of the component
     * @property {object} element - The DOM element that serves as the root of the component.
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} Search A reference to the instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.clearFilters = this.element.querySelectorAll(SELECTORS.CLEAR_FILTERS);
    this.showFilters = this.element.querySelector(SELECTORS.SHOW_FILTERS);
    this.appliedTags = this.element.querySelector(
      SELECTORS.LISTING_APPLIED_FILTERS
    );
    this.searchFilters = this.element.querySelector(SELECTORS.LISTING_FILTERS);
    this.closeFilters = this.element.querySelector(SELECTORS.CLOSE);
    this.showResults = this.element.querySelector(SELECTORS.SHOW_RESULTS);
    this.body = document.querySelector(SELECTORS.BODY);
    this.noResults = this.element.querySelector(
      SELECTORS.LIST_ITEMS_NO_RESULTS
    );
    this.pagination = this.element.querySelector(SELECTORS.PAGINATION);

    return this;
  }

  /**
   * Creates event listeners to enable interaction with view
   * @returns {object} Search A reference to the instance of the class
   */
  enable() {
    if (this.clearFilters) {
      this.clearFilters.forEach((filter) => {
        filter.addEventListener(EVENTS.CLICK, this.constructor.onClick);
      });
    }
    if (this.showFilters) {
      this.showFilters.addEventListener(EVENTS.CLICK, this.onToggle.bind(this));
    }
    if (this.closeFilters) {
      this.closeFilters.addEventListener(
        EVENTS.CLICK,
        this.onToggle.bind(this)
      );
    }
    if (this.showResults) {
      this.showResults.addEventListener(EVENTS.CLICK, this.onToggle.bind(this));
    }
    document.addEventListener(
      EVENTS.FACETWP_LOADED,
      this.facetUpdates.bind(this)
    );
    document.addEventListener(
      EVENTS.FACETWP_REFRESH,
      this.constructor.facetRefresh.bind(this)
    );

    return this;
  }

  /**
   * Clear applied filters on click event.
   */
  static onClick() {
    const allFacets = Object.keys(FWP.facets);
    FWP.reset(allFacets);
  }

  /**
   * Toggles the search filter modal open.
   */
  onToggle() {
    this.searchFilters.classList.toggle(CLASS_NAMES.ACTIVE);
    this.body.classList.toggle(CLASS_NAMES.MODAL_MOBILE);
  }

  /**
   * Triggers on facets being applied.
   * When facets are applied toggles
   * the clear filters button, the selected
   * filters label, and, if no results, the
   * no results message.
   */
  facetUpdates() {
    const facetObject = FWP.facets;
    const allFacets = Object.keys(facetObject);
    const sortIndex = allFacets.indexOf('sort_by');
    const caseSortIndex = allFacets.indexOf('case_sort_by');

    this.constructor.addClearSearchbox(this.element);

    if (sortIndex > -1) {
      allFacets.splice(sortIndex, 1);
    }

    if (caseSortIndex > -1) {
      allFacets.splice(caseSortIndex, 1);
    }

    const appliedFacets = allFacets.filter(
      (facet) => facetObject[facet].length > 0
    );

    // Hide pagination if no results are returned.
    if (this.noResults) {
      this.pagination.classList.add(CLASS_NAMES.HIDDEN);
    } else {
      this.pagination.classList.remove(CLASS_NAMES.HIDDEN);
    }

    // Render or hide filters tag section if applied.
    if (this.appliedTags) {
      if (appliedFacets.length > 0) {
        this.appliedTags.classList.add(CLASS_NAMES.ACTIVE);
      } else {
        this.appliedTags.classList.remove(CLASS_NAMES.ACTIVE);
      }
    }

    // Scroll to top of page on page click.
    if (FWP.enable_scroll === true) {
      this.element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  }

  /**
   * Add clear button to keyword searchbox.
   * @param {HTMLElement} element - REQUIRED - the search element
   */
  static addClearSearchbox(element) {
    const searchbox = element.querySelector(SELECTORS.FACETWP_SEARCH);
    if (searchbox) {
      if (!searchbox.nextElementSibling) {
        const clearSearch = document.createElement('button');
        clearSearch.title = 'Clear Search';
        clearSearch.classList.add(CLASS_NAMES.FACETWP_CLEAR_SEARCH);
        clearSearch.addEventListener(EVENTS.CLICK, () => {
          searchbox.value = '';
          FWP.autoload();
        });
        searchbox.parentNode.appendChild(clearSearch);
      }

      if (searchbox.nextElementSibling) {
        if (searchbox.value === '') {
          searchbox.nextElementSibling.classList.add(CLASS_NAMES.HIDDEN);
        } else if (searchbox.value !== '') {
          searchbox.nextElementSibling.classList.remove(CLASS_NAMES.HIDDEN);
        }
      }
    }
  }

  /**
   * Triggers on facets refreshing.
   * Set to true to enable scrolling to top
   * of the page on pagination click.
   */
  static facetRefresh() {
    if (FWP.soft_refresh === true) {
      FWP.enable_scroll = true;
    } else {
      FWP.enable_scroll = false;
    }
  }
}
